import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { HeroSplit } from '../components/HeroSplit';
import { ArticleCard } from '../components/ArticleCard';
import { VideoCard } from '../components/VideoCard';
import { ShortPill } from '../components/ShortPill';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const [covers, setCovers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [articlesPage, setArticlesPage] = useState(1);
  const [videosPage, setVideosPage] = useState(1);
  const [hasMoreArticles, setHasMoreArticles] = useState(true);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Refs for scrolling
  const articlesSectionRef = useRef(null);
  const videosSectionRef = useRef(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const [coversRes, articlesRes, videosRes, shortsRes] = await Promise.all([
          axios.get(`${API_URL}/api/covers?featured=true&pageSize=5`),
          axios.get(`${API_URL}/api/articles?page=1&pageSize=6`),
          axios.get(`${API_URL}/api/videos?page=1&pageSize=4`),
          axios.get(`${API_URL}/api/shorts?pageSize=6`),
        ]);

        setCovers(coversRes.data);
        setArticles(articlesRes.data);
        setVideos(videosRes.data);
        setShorts(shortsRes.data);
        setHasMoreArticles(articlesRes.data.length === 6);
        setHasMoreVideos(videosRes.data.length === 4);
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const loadMoreArticles = async () => {
    try {
      setLoadingMore(true);
      const nextPage = articlesPage + 1;
      const response = await axios.get(`${API_URL}/api/articles?page=${nextPage}&pageSize=6`);
      
      if (response.data.length > 0) {
        setArticles([...articles, ...response.data]);
        setArticlesPage(nextPage);
        setHasMoreArticles(response.data.length === 6);
        
        // Scroll to articles section after loading
        setTimeout(() => {
          articlesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setHasMoreArticles(false);
      }
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreVideos = async () => {
    try {
      setLoadingMore(true);
      const nextPage = videosPage + 1;
      const response = await axios.get(`${API_URL}/api/videos?page=${nextPage}&pageSize=4`);
      
      if (response.data.length > 0) {
        setVideos([...videos, ...response.data]);
        setVideosPage(nextPage);
        setHasMoreVideos(response.data.length === 4);
        
        // Scroll to videos section after loading
        setTimeout(() => {
          videosSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setHasMoreVideos(false);
      }
    } catch (error) {
      console.error('Error loading more videos:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const renderSkeletons = (count, type = 'article') => {
    return Array.from({ length: count }).map((_, i) => (
      <div key={i} className="space-y-3">
        <Skeleton className="w-full aspect-[16/10] rounded-lg" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    ));
  };

  return (
    <>
      <Helmet>
        <title>B&B Magazine - Brands & Bloggers</title>
        <meta name="description" content="Bold voices. Braver brands. Interviews, features, and series at the intersection of creators and commerce." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com" />
      </Helmet>

      <HeaderNav />

      <main>
        {/* Hero Section */}
        <HeroSplit covers={covers} />

        {/* Featured Articles Section */}
        <section className="w-full py-14 md:py-20">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                Featured Content
              </span>
              <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold">
                Latest Stories
              </h2>
            </div>

            {/* Tabs for filtering content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-8 bg-[hsl(var(--surface-2))] p-1">
                <TabsTrigger value="all" className="font-ui">All</TabsTrigger>
                <TabsTrigger value="articles" className="font-ui">Articles</TabsTrigger>
                <TabsTrigger value="videos" className="font-ui">Videos</TabsTrigger>
                <TabsTrigger value="shorts" className="font-ui">Shorts</TabsTrigger>
              </TabsList>

              {/* All Content */}
              <TabsContent value="all">
                {loading ? (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {renderSkeletons(6)}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-12">
                      {/* Articles */}
                      {articles.length > 0 && (
                        <div ref={articlesSectionRef}>
                          <h3 className="font-display text-2xl font-bold mb-6">Latest Articles</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {articles.map((article) => (
                              <ArticleCard key={article.id} article={article} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Videos */}
                      {videos.length > 0 && (
                        <div ref={videosSectionRef}>
                          <h3 className="font-display text-2xl font-bold mb-6">Latest Videos</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {videos.map((video) => (
                              <VideoCard key={video.id} video={video} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Load More Buttons */}
                    <div className="space-y-4 mt-10">
                      {hasMoreArticles && (
                        <div className="flex justify-center">
                          <Button 
                            onClick={loadMoreArticles} 
                            disabled={loadingMore}
                            variant="outline"
                            size="lg"
                          >
                            {loadingMore ? 'Loading...' : 'Load More Articles'}
                          </Button>
                        </div>
                      )}
                      {hasMoreVideos && (
                        <div className="flex justify-center">
                          <Button 
                            onClick={loadMoreVideos} 
                            disabled={loadingMore}
                            variant="outline"
                            size="lg"
                          >
                            {loadingMore ? 'Loading...' : 'Load More Videos'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Articles Only */}
              <TabsContent value="articles">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {renderSkeletons(6)}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {articles.map((article) => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                    </div>
                    {hasMoreArticles && (
                      <div className="flex justify-center mt-10">
                        <Button 
                          onClick={loadMoreArticles} 
                          disabled={loadingMore}
                          variant="outline"
                          size="lg"
                        >
                          {loadingMore ? 'Loading...' : 'Load More Stories'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Videos Only */}
              <TabsContent value="videos">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderSkeletons(4)}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {videos.map((video) => (
                        <VideoCard key={video.id} video={video} />
                      ))}
                    </div>
                    {hasMoreVideos && (
                      <div className="flex justify-center mt-10">
                        <Button 
                          onClick={loadMoreVideos} 
                          disabled={loadingMore}
                          variant="outline"
                          size="lg"
                        >
                          {loadingMore ? 'Loading...' : 'Load More Videos'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Shorts Only */}
              <TabsContent value="shorts">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderSkeletons(6, 'short')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shorts.map((short) => (
                      <ShortPill key={short.id} short={short} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}